import { useState, useEffect } from 'react'
import { useAuthStore } from '../../store/authStore'
import {
  Mail, Phone, User as UserIcon, MapPin, Heart, BadgeCheck,
  Sparkles, CalendarCheck, FileText, CalendarDays, Info, Camera, Shield,
  Copy, Check, Briefcase,
} from 'lucide-react'
import { attendanceAPI, reportAPI, leaveAPI, authAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'

interface EmployeeProfileDetails {
  father_husband_name?: string
  dob?: string
  gender?: string
  blood_group?: string
  aadhaar_number?: string
  address?: string
  district?: number | null
  district_name?: string | null
  training_centre?: number | null
  training_centre_name?: string | null
  designation?: string
  emergency_contact_name?: string
  emergency_contact_number?: string
  employment_status?: string
}

const EMPLOYMENT_STATUS_STYLES: Record<string, { chip: string; dot: string }> = {
  ACTIVE:     { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500' },
  ON_LEAVE:   { chip: 'bg-blue-50 text-blue-700 ring-blue-200',          dot: 'bg-blue-500' },
  SUSPENDED:  { chip: 'bg-orange-50 text-orange-700 ring-orange-200',    dot: 'bg-orange-500' },
  TERMINATED: { chip: 'bg-red-50 text-red-700 ring-red-200',             dot: 'bg-red-500' },
}

const DEFAULT_STATUS = { chip: 'bg-neutral-100 text-neutral-700 ring-neutral-200', dot: 'bg-neutral-400' }

export default function ProfilePage() {
  const { user } = useAuthStore()
  const { error: toastError } = useToast()
  const [counts, setCounts] = useState({ attendance: 0, reports: 0, leave: 0 })
  const [profile, setProfile] = useState<EmployeeProfileDetails | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingProfile, setIsLoadingProfile] = useState(true)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  useEffect(() => {
    authAPI
      .getCurrentUser()
      .then((res) => setProfile(res.data.profile || null))
      .catch((err) => toastError(getErrorMessage(err, 'Failed to load full profile details.')))
      .finally(() => setIsLoadingProfile(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (user?.role !== 'employee') {
      setIsLoading(false)
      return
    }
    const load = async () => {
      try {
        const [attendanceRes, reportsRes, leaveRes] = await Promise.all([
          attendanceAPI.getHistory(),
          reportAPI.getHistory(),
          leaveAPI.getMyRequests(),
        ])
        const countOf = (data: any) => (Array.isArray(data) ? data.length : data.count ?? (data.results?.length || 0))
        setCounts({
          attendance: countOf(attendanceRes.data),
          reports: countOf(reportsRes.data),
          leave: countOf(leaveRes.data),
        })
      } catch {
        // leave counts at 0 if this fails
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [user?.role])

  const handleCopy = (value: string, field: string) => {
    if (!value) return
    navigator.clipboard.writeText(value)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 1500)
  }

  const statusStyle = profile?.employment_status
    ? EMPLOYMENT_STATUS_STYLES[profile.employment_status] || DEFAULT_STATUS
    : null

  // const hasEmergency = profile?.emergency_contact_name || profile?.emergency_contact_number
  const roleLabel =
    user?.role === 'admin' ? 'Administrator' :
    user?.role === 'viewer' ? 'Viewer' :
    'Employee'

  // ── Section header ──
  const SectionHeader = ({
    icon: Icon, title, gradient, subtitle,
  }: { icon: any; title: string; gradient: string; subtitle?: string }) => (
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
          {title}
        </h3>
        {subtitle && <p className="text-[11px] text-neutral-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  )

  // ── Info row ──
  const InfoRow = ({
    icon: Icon, label, value, copyValue, bordered = true,
  }: {
    icon: any; label: string; value: React.ReactNode; copyValue?: string; bordered?: boolean
  }) => (
    <div className={`group flex items-center gap-4 py-3.5 ${bordered ? 'border-b border-neutral-100' : ''}`}>
      <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0 group-hover:bg-[#E4670F]/10 transition-colors">
        <Icon className="w-4 h-4 text-neutral-500 group-hover:text-[#C24A0A] transition-colors" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">
          {label}
        </p>
        <p className="text-sm font-semibold text-neutral-900 truncate">{value || '—'}</p>
      </div>
      {copyValue && (
        <button
          onClick={() => handleCopy(copyValue, label)}
          className="opacity-0 group-hover:opacity-100 p-1.5 text-neutral-400 hover:text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-lg transition-all flex-shrink-0"
          title="Copy"
        >
          {copiedField === label ? (
            <Check className="w-3.5 h-3.5 text-emerald-600" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
        </button>
      )}
    </div>
  )

  // ── Stat card ──
  const StatCard = ({
    icon: Icon, label, value, gradient, glow, ring,
  }: {
    icon: any; label: string; value: number; gradient: string; glow: string; ring: string
  }) => (
    <div className={`group relative bg-white rounded-2xl ring-1 ring-neutral-200/70 p-5 hover:shadow-lg ${ring} transition-all duration-300 overflow-hidden`}>
      <div className={`absolute top-0 right-0 w-20 h-20 ${glow} rounded-full blur-2xl transition-colors`} />
      <div className="relative">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shadow-md mb-3 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <p className="text-3xl font-bold text-neutral-900 tabular-nums leading-none">{value}</p>
        <p className="text-xs font-medium text-neutral-500 mt-1.5">{label}</p>
      </div>
    </div>
  )

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      <div className=" mx-auto">
        {/* ── Page Header ── */}
        <div className="mb-7">
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Account
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            My Profile
          </h1>
          <p className="text-sm text-neutral-500">View your account and employment information</p>
        </div>

        {/* ══ HERO CARD ══ */}
        <div className="relative bg-white rounded-3xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden mb-6">
          {/* Gradient banner */}
          <div className="relative h-32 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] overflow-hidden">
            <div className="absolute -top-16 -left-16 w-72 h-72 rounded-full bg-[#F0821F]/30 blur-3xl" />
            <div className="absolute -bottom-16 right-1/4 w-72 h-72 rounded-full bg-[#A31621]/30 blur-3xl" />
            <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
                backgroundSize: '32px 32px',
              }}
            />
            {/* Role pill — top right of banner */}
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm text-[#C24A0A] ring-1 ring-white/40 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
                <Shield className="w-3 h-3" />
                {roleLabel}
              </span>
            </div>
          </div>

          {/* Content below banner */}
          <div className="px-6 sm:px-8 py-7">
            {/* Identity row — avatar + name side by side, no overlap */}
            <div className="flex items-start gap-5 mb-8">
              {/* Avatar */}
              <div className="relative flex-shrink-0 -mt-16">
                {user?.photo ? (
                  <img
                    src={user.photo}
                    alt={user.full_name}
                    className="w-24 h-24 rounded-2xl object-cover ring-4 ring-white shadow-xl"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center text-white text-4xl font-bold ring-4 ring-white shadow-xl">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
                {user?.role === 'employee' && (
                  <div
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-xl bg-white ring-2 ring-neutral-100 flex items-center justify-center shadow-md"
                    title="Photo is managed by an administrator"
                  >
                    <Camera className="w-3.5 h-3.5 text-neutral-400" />
                  </div>
                )}
              </div>

              {/* Name + role + ID */}
              <div className="flex-1 min-w-0 pt-1">
                <h2
                  className="text-2xl sm:text-3xl font-bold text-neutral-900 truncate leading-tight"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  {user?.full_name}
                </h2>

                {/* Meta chips — role, ID, status */}
                <div className="flex items-center gap-2 flex-wrap mt-3">
                  {/* Role chip */}
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#E4670F]/10 text-[#C24A0A] ring-1 ring-[#E4670F]/20">
                    <Briefcase className="w-3 h-3" />
                    {profile?.designation || roleLabel}
                  </span>

                  {/* Employee ID chip */}
                  {user?.employee_id && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200 tabular-nums">
                      <BadgeCheck className="w-3 h-3 text-neutral-500" />
                      {user.employee_id}
                    </span>
                  )}

                  {/* Employment status chip */}
                  {statusStyle && profile?.employment_status && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ring-1 ${statusStyle.chip}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                      {profile.employment_status.replace('_', ' ')}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Info sections — 2-column grid on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-7">
              {/* ── Contact & Identity ── */}
              <div>
                <SectionHeader
                  icon={UserIcon}
                  title="Contact & Identity"
                  gradient="from-[#F0821F] to-[#A31621]"
                  subtitle="How we reach you"
                />
                <InfoRow
                  icon={Mail}
                  label="Email Address"
                  value={user?.email}
                  copyValue={user?.email}
                />
                <InfoRow
                  icon={Phone}
                  label="Mobile"
                  value={user?.mobile}
                  copyValue={user?.mobile}
                  bordered={false}
                />
              </div>

              {/* ── Emergency Contact ──
              {!isLoadingProfile && profile && hasEmergency ? (
                <div>
                  <SectionHeader
                    icon={Heart}
                    title="Emergency Contact"
                    gradient="from-red-500 to-red-700"
                    subtitle="Who to reach in an emergency"
                  />
                  <InfoRow
                    icon={UserIcon}
                    label="Contact Name"
                    value={profile.emergency_contact_name}
                  />
                  <InfoRow
                    icon={Phone}
                    label="Contact Number"
                    value={profile.emergency_contact_number}
                    copyValue={profile.emergency_contact_number}
                    bordered={false}
                  />
                </div>
              ) : !isLoadingProfile && user?.role === 'employee' ? (
                <div>
                  <SectionHeader
                    icon={Heart}
                    title="Emergency Contact"
                    gradient="from-neutral-300 to-neutral-400"
                    subtitle="Not yet provided"
                  />
                  <div className="flex items-start gap-2.5 bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-3 text-xs text-neutral-600">
                    <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-neutral-400" />
                    <span>No emergency contact on file. Contact an administrator to add one.</span>
                  </div>
                </div>
              ) : null} */}
            </div>

            {/* Loading spinner */}
            {isLoadingProfile && (
              <div className="flex items-center justify-center py-6 mt-4">
                <div className="flex items-center gap-3 text-sm text-neutral-500">
                  <div className="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
                  Loading additional details...
                </div>
              </div>
            )}

            {/* Info banner */}
            {user?.role === 'employee' && (
              <div className="mt-7 flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
                <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                <span>
                  <strong className="font-semibold">Need to update your details?</strong>{' '}
                  Contact an administrator — most of this information is managed centrally.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ══ ACTIVITY SUMMARY ══ */}
        {user?.role === 'employee' && (
          <div className="bg-white rounded-3xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
            <div className="px-6 sm:px-7 py-5 border-b border-neutral-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  Account Activity
                </h3>
                <p className="text-xs text-neutral-500">Your contributions so far</p>
              </div>
            </div>

            <div className="p-6 sm:p-7">
              {isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
                    <p className="text-sm text-neutral-500">Loading activity...</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <StatCard
                    icon={CalendarCheck}
                    label="Attendance Records"
                    value={counts.attendance}
                    gradient="from-blue-500 to-blue-700"
                    glow="bg-blue-500/[0.04] group-hover:bg-blue-500/[0.08]"
                    ring="hover:ring-blue-400/40 hover:shadow-blue-500/5"
                  />
                  <StatCard
                    icon={FileText}
                    label="Reports Submitted"
                    value={counts.reports}
                    gradient="from-emerald-500 to-emerald-700"
                    glow="bg-emerald-500/[0.04] group-hover:bg-emerald-500/[0.08]"
                    ring="hover:ring-emerald-400/40 hover:shadow-emerald-500/5"
                  />
                  <StatCard
                    icon={CalendarDays}
                    label="Leave Applications"
                    value={counts.leave}
                    gradient="from-violet-500 to-violet-800"
                    glow="bg-violet-500/[0.04] group-hover:bg-violet-500/[0.08]"
                    ring="hover:ring-violet-400/40 hover:shadow-violet-500/5"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}